import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParametres } from '../contexts/ParametresContext';

import {
  ArrowLeft, Plus, Pencil, Trash2, X,
  GripVertical, Eye, ArrowRight, Settings, BookOpen,
  TrendingUp, Users, ChevronRight, ChevronDown,
  Baby, School, GraduationCap, Building, Layers, PencilRuler,
  Info, Wallet, Bus, Utensils, Clock, Gauge,
  ListChecks, RefreshCw
} from 'lucide-react';

// ✅ Drag & Drop (react-beautiful-dnd)
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { Toast } from './ui/Toast';
import { api } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import LoadingSpinner from './LoadingSpinner';
import ModalTauxHoraireClasse from './ModalTauxHoraireClasse';
import CaseACocher from './ui/CaseACocher';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from './ui/dialog';
import { formaterFCFA } from '../utils/montants';
import { CONFIRM_DIALOG_OVERLAY_CLASS } from './ui/modalOverlay';
import {
  DROITS_FERMES,
  droitsVerifies,
  estRefusDeDroit,
  marquerClasseSansMatiere,
  messageRefus,
  permissionsRequises,
  texteBandeauAnnexes,
  texteBandeauAssociationsClasse,
  texteBandeauSections,
  texteBandeauSeuil
} from './droitsClassesMatieres';

// Aides écrites sous un champ que l'agent ne peut pas modifier.
const AIDE_SEUIL_CREATION = `Le seuil par défaut s'appliquera : modifier un seuil demande ${permissionsRequises(['CLASSES.SEUILS.UPDATE'])}.`;
const AIDE_SEUIL_MODIFICATION = `Modifier le seuil demande ${permissionsRequises(['CLASSES.SEUILS.UPDATE'])}.`;
// La fenêtre « Modifier la classe » (convention des modals, DESIGN-SYSTEM.md).
const CHAMP_FENETRE = 'h-9 w-full rounded-lg border border-gray-200 px-3 text-sm text-gray-900 outline-none transition '
  + 'focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
const BOUTON_PIED_SECONDAIRE = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-lg border border-gray-300 '
  + 'bg-white px-4 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';
const BOUTON_PIED_PRINCIPAL = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-lg bg-[#246990] px-4 '
  + 'text-sm font-medium text-white transition hover:bg-[#1f5a7a] disabled:cursor-not-allowed disabled:opacity-50';
// Un frais en FCFA entiers pour le champ (« 2000.00 » devient « 2000 ») ; absent : vide.
const montantEntier = (valeur) => (
  valeur === null || valeur === undefined || valeur === '' ? '' : String(Math.round(Number(valeur) || 0))
);
const AIDE_SANS_MATIERE = `Marquer une classe sans matière demande ${permissionsRequises(['CLASSES.ASSOCIATIONS.MANAGE'])}.`;

// ----------------------------------------------------------------------------
// Blocs déclarés dans le composant
// ----------------------------------------------------------------------------
// Les blocs de cette page (cartes, listes, fenêtres, onglets du détail) sont
// déclarés à l'intérieur du composant pour lire son état. Écrits comme de
// simples fonctions, React les voyait comme des composants neufs à chaque
// rendu : il les détruisait puis les reconstruisait (clignotement, saisie et
// réordonnancement perdus). Ce crochet leur donne une identité stable, tout en
// leur faisant lire l'état à jour.
const useComposantStable = (rendu) => {
  const renduRef = useRef(rendu);
  renduRef.current = rendu;
  return useMemo(() => {
    const ComposantStable = (props) => renduRef.current(props);
    return ComposantStable;
  }, []);
};

const getActiveSchoolYearId = () => {
  const storedYear = localStorage.getItem('annee_scolaire_id');
  if (storedYear) {
    const parsed = parseInt(storedYear, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userYear = user.annee_scolaire_active || user.annee_scolaire_id;
    const parsed = parseInt(userYear, 10);
    return Number.isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
};

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback
);

const getResultMessage = (result, fallback) => (
  result?.message ||
  result?.error ||
  fallback
);

// `droits` vient de GET /api/classes/droits (ClasseMatieres) : fermé par
// défaut, il cache chaque geste d'écriture que le serveur refuserait.
const ClassesModernes = ({ estActif = true, droits = DROITS_FERMES, onRefusDroit } = {}) => {
  // Les bandeaux « il faut la permission » ne parlent que de droits lus : si la
  // lecture a échoué, ClasseMatieres le dit lui-même, une seule fois.
  const bandeau = (texte) => (droitsVerifies(droits) && texte ? (
    <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">{texte}</p>
  ) : null);

  // ============================================================================
// ÉTATS DE BASE
// ============================================================================

  // 🧠 On ne gère PLUS les sections ici : on les lit depuis le Provider
  const {
    parametres,
    loading: loadingParametres,
    formatPrice,
    getSectionColors,
    getSectionById,
  } = useParametres();


// Calculées au rendu : l'ancien couple état + effet ajoutait un premier
// affichage sans aucune section, donc une page vide avant la roue.
const sectionsStables = useMemo(
  () => (parametres.sections || []).filter(s => s.active === true),
  [parametres.sections]
);



  // États principaux
  const [classes, setClasses] = useState([]);
  const [classesBySection, setClassesBySection] = useState({});
  const [matieres, setMatieres] = useState([]);
  const [loading, setLoading] = useState(false);
  // Vrai jusqu'à la fin du premier chargement : la roue s'affiche d'emblée.
  const [chargementInitial, setChargementInitial] = useState(true);
  const [hasCantine, setHasCantine] = useState(true);

  const getSectionIcon = (sectionOrId, className = 'w-5 h-5') => {
    const section = typeof sectionOrId === 'object' && sectionOrId !== null
      ? sectionOrId
      : getSectionById(sectionOrId);
    const sectionName = String(section?.nom || '').toLowerCase();

    let Icon = Layers;
    if (sectionName.includes('préscolaire') || sectionName.includes('prescolaire') || sectionName.includes('maternelle')) {
      Icon = Baby;
    } else if (sectionName.includes('primaire') || sectionName.includes('élémentaire') || sectionName.includes('elementaire')) {
      Icon = PencilRuler;
    } else if (sectionName.includes('collège') || sectionName.includes('college')) {
      Icon = School;
    } else if (sectionName.includes('lycée') || sectionName.includes('lycee') || sectionName.includes('secondaire')) {
      Icon = GraduationCap;
    } else if (sectionName.includes('administration') || sectionName.includes('bureau')) {
      Icon = Building;
    }

    return <Icon className={className} strokeWidth={1.9} aria-hidden="true" />;
  };

  const isClasseSansMatiere = (classe) => (
    classe?.sans_matiere === true ||
    classe?.sans_matiere === 1 ||
    classe?.sans_matiere === '1' ||
    classe?.sans_matiere === 'true'
  );

  // États d'interface
  const [activeView, setActiveView] = useState('dashboard'); // 'dashboard' | 'sections' | 'creation' | 'details'
  const [selectedSection, setSelectedSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set());

  // États de création/édition
  const [creationData, setCreationData] = useState({
    section_id: '',
    nom: '',
    frais_inscription: '',
    frais_reinscription: '',
    frais_mensuel: '',
    frais_transport: '',
    frais_cantine: '',
    taux_horaire: '',
    seuil_eleves: '',
    sans_matiere: false,
    ordre_suggere: 1,
    progression_preview: null
  });

  const [editingClasse, setEditingClasse] = useState(null);
  // La classe dont on demande un nouveau taux horaire (demande validée au Centre de Support).
  const [tauxClasse, setTauxClasse] = useState(null);

  // États pour la réorganisation
  const [reorderMode, setReorderMode] = useState(false);

  // États modales

  // ✅ NOUVELLE GESTION TOAST COMPATIBLE
  const [currentToast, setCurrentToast] = useState(null);

  const addToast = useCallback((message, type = 'success') => {
    setCurrentToast({ message, type });
  }, []);

  const closeToast = useCallback(() => {
    setCurrentToast(null);
  }, []);

  // Le message d'une écriture refusée. Un refus de permission du serveur se dit
  // en clair, et les droits sont relus aussitôt (onRefusDroit) : l'écran retire
  // alors le geste qui vient d'être refusé.
  const messageEchec = (result, fallback) => {
    if (estRefusDeDroit(result)) {
      onRefusDroit?.();
      return messageRefus(result);
    }
    return getResultMessage(result, fallback);
  };

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: null,
    type: 'info'
  });

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false, onConfirm: null }));
  };

  // 🆕 ÉTATS NOUVEAUX MODALS
  const [showSeuilGlobalModal, setShowSeuilGlobalModal] = useState(false);
  const [selectedClasseDetails, setSelectedClasseDetails] = useState(null);
  const [classeDetailsData, setClasseDetailsData] = useState(null);
  const [activeClasseDetailTab, setActiveClasseDetailTab] = useState('infos');
  const [classeMatieresModal, setClasseMatieresModal] = useState({
    isOpen: false,
    classe: null,
    initialMatiereIds: [],
    selectedMatiereIds: [],
    saving: false
  });
  const [classChoiceModal, setClassChoiceModal] = useState({
    isOpen: false,
    title: '',
    description: '',
    items: [],
    actionLabel: '',
    variant: 'matieres',
    onSelect: null
  });

  const openClassChoiceModal = useCallback((config) => {
    setClassChoiceModal({
      isOpen: true,
      title: config.title,
      description: config.description,
      items: config.items || [],
      actionLabel: config.actionLabel || 'Ouvrir',
      variant: config.variant || 'matieres',
      onSelect: config.onSelect || null
    });
  }, []);

  const closeClassChoiceModal = useCallback(() => {
    setClassChoiceModal({
      isOpen: false,
      title: '',
      description: '',
      items: [],
      actionLabel: '',
      variant: 'matieres',
      onSelect: null
    });
  }, []);
  const [seuilGlobal, setSeuilGlobal] = useState(25);
  const [autoCreationAnnexeSeuil, setAutoCreationAnnexeSeuil] = useState(false);

  /**
   * Valide les données de création
   */
  const validateCreationData = () => {
    const errors = [];
    if (!creationData.nom.trim()) errors.push('Le nom de la classe est requis');
    if (!creationData.section_id) errors.push('La section est requise');
    if (!creationData.frais_inscription || parseFloat(creationData.frais_inscription) < 0) {
      errors.push('Les frais d\'inscription doivent être positifs');
    }
    return errors;
  };

  // ============================================================================
// CHARGEMENT DES DONNÉES
// ============================================================================

  /**
   * Charge toutes les données de CLASSES en se basant sur les sections du Provider
   * 👉 On ne touche plus aux paramètres ici (le Provider s’en charge)
   */
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sectionResults, matieresData] = await Promise.all([
        Promise.all(
          sectionsStables.map(async (section) => {
            try {
              const sectionClassesResponse = await api.classes.getBySection(section.id);
              const sectionClasses = sectionClassesResponse.success ? sectionClassesResponse.data : [];
              return [section.id, sectionClasses];
            } catch (err) {
              console.warn(`Erreur chargement section ${section.id}:`, err);
              return [section.id, []];
            }
          })
        ),
        api.matieres.getAll(getActiveSchoolYearId())
      ]);

      const grouped = Object.fromEntries(sectionResults);
      const allClasses = sectionResults.flatMap(([, sectionClasses]) => sectionClasses);

      setClassesBySection(grouped);
      setClasses(allClasses);
      setMatieres(Array.isArray(matieresData) ? matieresData : []);
    } catch (err) {
      console.error('Erreur chargement donnees:', err);
      addToast(getErrorMessage(err, 'Erreur lors du chargement'), 'error');
    } finally {
      setLoading(false);
      setChargementInitial(false);
    }
  }, [addToast, sectionsStables]);
  const loadSeuilGlobal = useCallback(async () => {
  try {
    const result = await api.classes.getSeuilGlobal();
    if (result.success) {
      setSeuilGlobal(result.data.seuil_global);
      setAutoCreationAnnexeSeuil(Boolean(result.data.auto_creation_annexe_seuil));
      setCreationData(prev => ({
        ...prev,
        seuil_eleves: result.data.seuil_global
      }));
    }
  } catch (error) {
    console.error('Erreur chargement seuil global:', error);
  }
}, []);

  // 🆕 DÉTAILS CLASSE
  const handleViewClasseDetails = async (classeId) => {
    try {
      setLoading(true);
      const result = await api.classes.getDetailsComplets(classeId);
      if (result.success) {
        setClasseDetailsData(result.data);
        setSelectedClasseDetails(classeId);
        setActiveClasseDetailTab('infos');
        setActiveView('details');
      } else {
        addToast(getResultMessage(result, 'Erreur lors du chargement'), 'error');
      }
    } catch (error) {
      console.error('Erreur détails classe:', error);
      addToast(getErrorMessage(error, 'Erreur lors du chargement des détails'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const reloadClasseDetails = async (classeId) => {
    try {
      const result = await api.classes.getDetailsComplets(classeId);
      if (result.success) {
        setClasseDetailsData(result.data);
      }
    } catch (error) {
      console.error('Erreur rechargement détails:', error);
    }
  };

  const openClasseMatieresModal = async (classeId, knownDetails = null) => {
    if (!droits.associer) return;
    const targetClasseId = parseInt(classeId, 10);
    if (!Number.isFinite(targetClasseId)) return;

    try {
      setLoading(true);
      const result = knownDetails || await api.classes.getDetailsComplets(targetClasseId);
      const data = knownDetails ? knownDetails : (result.success ? result.data : null);

      if (!data?.classe) {
        addToast('Impossible de charger les matières de cette classe', 'error');
        return;
      }

      const initialMatiereIds = (data.matieres || [])
        .map(matiere => parseInt(matiere.id, 10))
        .filter(id => Number.isFinite(id));

      setClasseMatieresModal({
        isOpen: true,
        classe: data.classe,
        initialMatiereIds,
        selectedMatiereIds: initialMatiereIds,
        saving: false
      });
    } catch (error) {
      console.error('Erreur ouverture association matières:', error);
      addToast(getErrorMessage(error, 'Erreur lors du chargement des matières de la classe'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const closeClasseMatieresModal = () => {
    setClasseMatieresModal(prev => (prev.saving ? prev : {
      ...prev,
      isOpen: false,
      classe: null,
      initialMatiereIds: [],
      selectedMatiereIds: []
    }));
  };

  const toggleMatiereForClasse = (matiereId) => {
    const normalizedId = parseInt(matiereId, 10);
    if (!Number.isFinite(normalizedId)) return;

    setClasseMatieresModal(prev => {
      const selected = new Set(prev.selectedMatiereIds);
      if (selected.has(normalizedId)) {
        selected.delete(normalizedId);
      } else {
        selected.add(normalizedId);
      }

      return {
        ...prev,
        selectedMatiereIds: Array.from(selected)
      };
    });
  };

  const saveClasseMatieresAssociations = async () => {
    const { classe, initialMatiereIds, selectedMatiereIds } = classeMatieresModal;
    if (!classe || !droits.associer) return;

    const initialSet = new Set(initialMatiereIds);
    const selectedSet = new Set(selectedMatiereIds);
    const toAdd = selectedMatiereIds.filter(id => !initialSet.has(id));
    const toRemove = initialMatiereIds.filter(id => !selectedSet.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      addToast('Aucune modification détectée', 'info');
      return;
    }

    addToast('Enregistrement des matières de la classe en cours...', 'loading');
    setClasseMatieresModal(prev => ({ ...prev, saving: true }));

    try {
      const activeYearId = getActiveSchoolYearId();
      const failures = [];
      let refus = null;

      for (const matiereId of toAdd) {
        const matiere = matieres.find(item => parseInt(item.id, 10) === parseInt(matiereId, 10));
        const result = await api.matieres.associations.add(
          matiereId,
          classe.id,
          activeYearId,
          matiere?.coefficient ?? null
        );

        if (!result.success) {
          if (!refus && estRefusDeDroit(result)) refus = result;
          failures.push(result.message || ('Erreur ajout matière ' + matiereId));
        }
      }

      for (const matiereId of toRemove) {
        const result = await api.matieres.associations.remove(matiereId, classe.id, activeYearId);
        if (!result.success) {
          if (!refus && estRefusDeDroit(result)) refus = result;
          failures.push(result.message || ('Erreur retrait matière ' + matiereId));
        }
      }

      await loadData();
      if (selectedClasseDetails && parseInt(selectedClasseDetails, 10) === parseInt(classe.id, 10)) {
        await reloadClasseDetails(classe.id);
      }

      // Refusé faute de permission : la fenêtre se ferme, elle ne peut plus
      // rien enregistrer.
      if (refus) {
        addToast(messageEchec(refus), 'error');
        setClasseMatieresModal({
          isOpen: false,
          classe: null,
          initialMatiereIds: [],
          selectedMatiereIds: [],
          saving: false
        });
        return;
      }

      if (failures.length > 0) {
        addToast(failures.slice(0, 2).join(' / ') + (failures.length > 2 ? '...' : ''), 'error');
        return;
      }

      addToast(toAdd.length + ' matière(s) ajoutée(s), ' + toRemove.length + ' retirée(s)', 'success');
      setClasseMatieresModal({
        isOpen: false,
        classe: null,
        initialMatiereIds: [],
        selectedMatiereIds: [],
        saving: false
      });
    } catch (error) {
      console.error('Erreur sauvegarde matières classe:', error);
      addToast(getErrorMessage(error, 'Erreur lors de la sauvegarde des matières de la classe'), 'error');
    } finally {
      setClasseMatieresModal(prev => (prev.isOpen ? { ...prev, saving: false } : prev));
    }
  };

  const refreshClasseMatterState = async (classeId) => {
    await loadData();
    if (selectedClasseDetails && parseInt(selectedClasseDetails, 10) === parseInt(classeId, 10)) {
      await reloadClasseDetails(classeId);
    }
  };

  const updateClasseDetailsMatterState = (classeId, patch = {}) => {
    setClasseDetailsData(prev => {
      if (!prev?.classe || parseInt(prev.classe.id, 10) !== parseInt(classeId, 10)) {
        return prev;
      }

      return {
        ...prev,
        classe: { ...prev.classe, ...patch.classe },
        matieres: patch.matieres !== undefined ? patch.matieres : prev.matieres
      };
    });
  };

  const applyClasseSansMatiere = async (classe, enabled, associatedMatieres = []) => {
    if (loading || !classe || !droits.associer) return;

    try {
      setLoading(true);
      addToast(
        enabled && associatedMatieres.length > 0
          ? 'Dissociation des matières en cours...'
          : enabled
            ? 'Activation classe sans matière en cours...'
            : 'Réactivation des matières en cours...',
        'loading'
      );

      // Un seul appel : le serveur retire les associations de la classe et la
      // marque dans la même transaction. L'ancienne bascule dissociait les
      // matières une à une puis renvoyait tout le formulaire de la classe.
      const result = await marquerClasseSansMatiere(classe.id, enabled);

      if (result?.success) {
        updateClasseDetailsMatterState(classe.id, {
          classe: { sans_matiere: enabled ? 1 : 0 },
          matieres: enabled ? [] : undefined
        });
        addToast(
          result.message || (enabled ? 'Classe marquée sans matière' : 'Matières réactivées pour cette classe'),
          'success'
        );
        await refreshClasseMatterState(classe.id);
      } else {
        addToast(messageEchec(result, 'Erreur lors de la mise à jour du statut matières'), 'error');
      }
    } catch (error) {
      addToast(getErrorMessage(error, 'Erreur lors de la mise à jour du statut matières'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const requestClasseSansMatiere = (classe, enabled, associatedMatieres = []) => {
    const safeMatieres = Array.isArray(associatedMatieres) ? associatedMatieres : [];

    if (enabled && safeMatieres.length > 0) {
      setConfirmDialog({
        isOpen: true,
        title: 'Marquer cette classe sans matière',
        description: 'Cette classe a ' + safeMatieres.length + ' matière(s) associée(s). Elles seront dissociées avant d’activer le statut sans matière. Continuer ?',
        confirmText: 'Dissocier et marquer',
        cancelText: 'Annuler',
        type: 'warning',
        onConfirm: async () => {
          closeConfirmDialog();
          await applyClasseSansMatiere(classe, true, safeMatieres);
        }
      });
      return;
    }

    applyClasseSansMatiere(classe, enabled, safeMatieres);
  };





    // Modifier une classe
 const handleUpdateClasse = async (classeId, formData) => {
  // ? Protection anti-spam
  if (loading) return;

  try {
    setLoading(true);
    addToast('Modification de la classe en cours...', 'loading');
    const result = await api.classes.update(classeId, formData);

    if (result.success) {
      addToast('Classe modifiée avec succès', 'success');
      await loadData(); // ? reload seulement en cas de succès
      if (activeView === 'details') {
        await reloadClasseDetails(classeId);
      }
      return result;
    } else {
      console.error("Erreur API:", result);
      // Refusée faute de permission : la fenêtre se ferme, le toast dit pourquoi.
      if (estRefusDeDroit(result)) setEditingClasse(null);
      addToast(messageEchec(result, 'Erreur lors de la modification'), 'error');
      return result;
    }

  } catch (error) {
    // ?? On cherche le vrai message backend
    const backendMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Erreur de validation des données';

    // ?? Affiche tout lobjet dd'erreur pour inspection
    console.error('Erreur handleUpdateClasse (détail complet):', error?.response?.data || error);

    // ?? Affiche le vrai message d'erreur côté utilisateur
    addToast(`Erreur lors de la mise à jour : ${backendMessage}`, 'error');
    return { success: false, message: backendMessage };
  } finally {
    setLoading(false);
  }
};


  // ✅ Préparer création (ne recharge plus les paramètres)
  const prepareCreation = async (sectionId) => {
    if (!droits.creer) return;
    try {
      setLoading(true);
      const preparation = await api.classes.preparerCreation(sectionId);
      if (preparation.success) {
        setCreationData(prev => ({
          ...prev,
          section_id: sectionId,
          ordre_suggere: preparation.data.ordre_suggere,
          progression_preview: preparation.data.classes_existantes
        }));
       const sel = sectionsStables.find(s => s.id === parseInt(sectionId));
        setSelectedSection(sel);
        setActiveView('creation');
      } else {
        addToast(getResultMessage(preparation, 'Erreur lors de la préparation'), 'error');
      }
    } catch (err) {
      console.error('❌ Erreur préparation création:', err);
      addToast(getErrorMessage(err, 'Erreur lors de la préparation'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
// EFFETS
// ============================================================================
// Chargement principal des classes après récupération des sections actives.
useEffect(() => {
  if (loadingParametres) return;
  if (sectionsStables.length > 0) {
    loadData();
    loadSeuilGlobal();
  } else {
    // Aucune section active : rien à charger, la roue ne doit pas rester.
    setChargementInitial(false);
  }
}, [loadingParametres, sectionsStables, loadData, loadSeuilGlobal]);

// L'onglet reste monté quand on passe sur Matières : à son retour, il se
// rafraîchit en arrière-plan, sans repasser par la roue de chargement.
const etaitActifRef = useRef(estActif);
useEffect(() => {
  if (estActif && !etaitActifRef.current && !loadingParametres && sectionsStables.length > 0) {
    loadData();
  }
  etaitActifRef.current = estActif;
}, [estActif, loadData, loadingParametres, sectionsStables]);





  /**
   * Calcule les stats par section (ne compte que les classes mères)
   */
  const calculateSectionStats = (sectionId) => {
    const classesSection = classesBySection[sectionId] || [];
    const classesMeres = classesSection.filter(classe => !classe.classe_mere_id);

    return {
      total_classes: classesMeres.length,
      total_eleves: classesMeres.reduce((acc, c) => acc + (parseInt(c.effectif || c.effectif_actuel || 0, 10) || 0), 0),
      progression_complete: classesMeres.length > 0 && classesMeres.every((classe, index) => parseFloat(classe.ordre) === (index + 1))
    };
  };


  // ============================================================================// GESTION CRÉATION CLASSE
// ============================================================================

  const handleCreateClasse = async () => {
  // ✅ Protection anti-spam
  if (loading) return;

  try {
    setLoading(true);
    const errors = validateCreationData();
    if (errors.length > 0) {
      addToast(errors.join(', '), 'error');
      return;
    }

    addToast('Création de la classe en cours...', 'loading');

    const result = await api.classes.create({
      nom: creationData.nom.trim(),
      section_id: creationData.section_id,
      frais_inscription: parseFloat(creationData.frais_inscription),
      frais_reinscription: parseFloat(creationData.frais_reinscription) || 0,
      frais_mensuel: parseFloat(creationData.frais_mensuel) || 0,
      frais_transport: parseFloat(creationData.frais_transport) || 0,
      frais_cantine: parseFloat(creationData.frais_cantine) || 0,
      taux_horaire: parseFloat(creationData.taux_horaire) || 0, // ✅ NOUVEAU CHAMP
      // Sans le droit des seuils, la classe suit le seuil par défaut (null) ;
      // sans celui des associations, elle n'est pas marquée sans matière. Le
      // serveur refuserait sinon toute la création.
      seuil_eleves: droits.seuils && creationData.seuil_eleves ? parseInt(creationData.seuil_eleves) : null,
      sans_matiere: droits.associer ? Boolean(creationData.sans_matiere) : false
    });

    if (result.success) {
      // ✅ Reset des données AVANT le reload
      setCreationData({
        section_id: '',
        nom: '',
        frais_inscription: '',
        frais_reinscription: '',
        frais_mensuel: '',
        frais_transport: '',
        frais_cantine: '',
        taux_horaire: '',
        seuil_eleves: seuilGlobal,
        sans_matiere: false,
        ordre_suggere: 1,
        progression_preview: null
      });
      setHasCantine(true);
      setActiveView('sections');

      addToast(`Classe "${creationData.nom}" créée avec succès (ordre: ${result.data?.ordre_attribue})`, 'success');

      // ✅ Reload seulement en cas de succès
      await loadData();
    } else {
      addToast(messageEchec(result, 'Erreur lors de la création'), 'error');
    }
  } catch (err) {
    console.error('❌ Erreur création classe:', err);
    addToast(getErrorMessage(err, 'Erreur lors de la création'), 'error');
  } finally {
    setLoading(false);
  }
};

  // ============================================================================
// GESTION DRAG & DROP + RÉORGANISATION
// ============================================================================

  const handleReorderClasses = async (sectionId, newOrder) => {
  // ✅ Protection anti-spam
  if (loading) return;

  try {
    setLoading(true);
    addToast('Réorganisation des classes en cours...', 'loading');
    const result = await api.classes.reorderSection(sectionId, newOrder);
    if (result.success) {
      const updatedSection = await api.classes.getBySection(sectionId);
      if (updatedSection.success) {
        setClassesBySection(prev => ({
          ...prev,
          [sectionId]: updatedSection.data
        }));
      }
      addToast('Réorganisation réussie', 'success');
    } else {
      addToast(messageEchec(result, 'Erreur lors de la réorganisation'), 'error');
    }
  } catch (err) {
    console.error('❌ Erreur réorganisation:', err);
    addToast(getErrorMessage(err, 'Erreur lors de la réorganisation'), 'error');
  } finally {
    setLoading(false);
  }
};

  const toggleReorderMode = (sectionId) => {
    setReorderMode(prev => prev === sectionId ? false : sectionId);
  };


    // ============================================================================
// COMPOSANT : CARTE SECTION AVEC CLASSES
// ============================================================================

  const SectionCard = useComposantStable(function SectionCard({ section, classes: sectionClasses }) {
    const colors = getSectionColors(section.id);
    const stats = calculateSectionStats(section.id);
    const primarySectionClasses = sectionClasses.filter(classe => !classe.classe_mere_id);
    const isExpanded = expandedSections.has(section.id);
    const isReordering = reorderMode === section.id && droits.reordonner;

    return (
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {/* Header de section */}
        <div
          className="p-6 cursor-pointer"
          style={{ backgroundColor: colors.light }}
          onClick={() => {
            const newExpanded = new Set(expandedSections);
            if (isExpanded) newExpanded.delete(section.id);
            else newExpanded.add(section.id);
            setExpandedSections(newExpanded);
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white shadow-sm"
                style={{ backgroundColor: colors.primary }}
              >
                {getSectionIcon(section, 'w-6 h-6')}
              </div>
              <div>
                <h3 className="text-xl font-semibold" style={{ color: colors.primary }}>
                  {section.nom}
                </h3>
                <p className="text-gray-600">
                  {stats.total_classes} classe(s)
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {stats.progression_complete ? (
                <div className="flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  Progression complète
                </div>
              ) : (
                <div className="flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                  A réorganiser
                </div>
              )}

              <div className="flex items-center space-x-2">
                {droits.creer && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      prepareCreation(section.id);
                    }}
                    className="p-2 text-white rounded-lg transition-colors"
                    style={{ backgroundColor: colors.primary }}
                    title="Ajouter une classe"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                )}

                {droits.reordonner && primarySectionClasses.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleReorderMode(section.id);
                    }}
                    className={`p-2 rounded-lg transition-colors ${
                      isReordering
                        ? 'bg-gray-800 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Réorganiser les classes"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="transform transition-transform" style={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
              }}>
                <ChevronDown className="w-5 h-5" style={{ color: colors.primary }} />
              </div>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t" style={{ borderColor: colors.secondary + '20' }}>
            {sectionClasses.length === 0 ? (
              <div className="p-8 text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                {droits.creer ? (
                  <>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">Aucune classe</h4>
                    <p className="text-gray-600 mb-4">
                      Commencez par créer votre première classe dans cette section
                    </p>
                    <button
                      onClick={() => prepareCreation(section.id)}
                      className="inline-flex items-center px-6 py-3 text-white font-semibold rounded-lg transition-colors"
                      style={{ backgroundColor: colors.primary }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Créer une classe
                    </button>
                  </>
                ) : (
                  <p className="text-gray-600">Aucune classe dans cette section.</p>
                )}
              </div>
            ) : isReordering ? (
              <ReorderInterface
                sectionId={section.id}
                classes={primarySectionClasses}
                colors={colors}
                onReorder={handleReorderClasses}
                onCancel={() => setReorderMode(false)}
              />
            ) : (
              <ClassesList
                classes={sectionClasses}
                colors={colors}
                onViewDetails={handleViewClasseDetails}
                onEdit={(classe) => {
                  setConfirmDialog({
                    isOpen: true,
                    title: 'Modifier la classe',
                    description: `Voulez-vous modifier la classe "${classe.nom}" ?`,
                    confirmText: 'Modifier',
                    cancelText: 'Annuler',
                    type: 'question',
                    onConfirm: async () => {
                      closeConfirmDialog();
                      setEditingClasse(classe);
                    }
                  });
                }}
                onDelete={(classeId) => {
                  const classe = sectionClasses.find(c => c.id === classeId);
                  setConfirmDialog({
                    isOpen: true,
                    title: 'Supprimer la classe',
                    description: `Êtes-vous sûr de vouloir supprimer la classe "${classe?.nom}" ? Cette action est irréversible.`,
                    confirmText: 'Supprimer',
                    cancelText: 'Annuler',
                    type: 'error',
                    onConfirm: async () => {
                      // ✅ Protection anti-spam
                      if (loading) return;
                      closeConfirmDialog();

                      try {
                        setLoading(true);
                        addToast('Suppression de la classe en cours...', 'loading');
                        const result = await api.classes.delete(classeId);
                        if (result.success) {
                          addToast('Classe supprimée avec succès', 'success');
                          // ✅ Reload seulement en cas de succès
                          await loadData();
                        } else {
                          addToast(messageEchec(result, 'Erreur lors de la suppression'), 'error');
                        }
                      } catch (err) {
                        addToast(getErrorMessage(err, 'Erreur lors de la suppression'), 'error');
                      } finally {
                        setLoading(false);
                      }
                    }
                  });
                }}
              />
            )}
          </div>
        )}
      </div>
    );
  });

  // ============================================================================
// COMPOSANT : LISTE DES CLASSES AVEC PROGRESSION
// ============================================================================

  const ClassesList = useComposantStable(function ClassesList({ classes, colors, onViewDetails, onEdit, onDelete }) {
    return (
      <div className="divide-y divide-gray-100">
        {classes
          .filter(classe => !classe.classe_mere_id)
          .map((classe, filteredIndex) => (
            <div key={classe.id} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: colors.primary }}
                  >
                    {Math.floor(classe.ordre)}
                  </div>

                  <div>
                    <div className="flex items-center space-x-3">
                      <h4 className="text-lg font-semibold text-gray-900">{classe.nom}</h4>

                      {classe.nombre_annexes > 0 && (
                        <span
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: colors.primary }}
                        >
                          {classe.nombre_annexes + 1} classes
                        </span>
                      )}

                      {filteredIndex < classes.filter(c => !c.classe_mere_id).length - 1 && (
                        <div className="flex items-center space-x-1 text-gray-400">
                          <ArrowRight className="w-4 h-4" />
                          <span className="text-sm">
                            {classes.filter(c => !c.classe_mere_id)[filteredIndex + 1]?.nom}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                      <span className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        {classe.effectif || 0} élève(s)
                        {classe.nombre_annexes > 0 && (
                          <span className="ml-1 text-gray-500">
                            (groupe: {classe.nombre_annexes + 1} classes)
                          </span>
                        )}
                      </span>
                      <span className="flex items-center">
                        {formatPrice(classe.frais_mensuel)}/mois
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button onClick={() => onViewDetails(classe.id)} title="Voir les détails" className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
                    <Eye className="w-4 h-4" />
                  </button>
                  {droits.modifier && (
                    <button onClick={() => onEdit(classe)} className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors" title="Modifier">
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {droits.supprimer && (
                    <button onClick={() => onDelete(classe.id)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>
    );
  });

  // ============================================================================
// COMPOSANT : INTERFACE DE RÉORGANISATION
// ============================================================================

  const ReorderInterface = useComposantStable(function ReorderInterface({ sectionId, classes, colors, onReorder, onCancel }) {
    const [reorderingClasses, setReorderingClasses] = useState([...classes]);
    const [hasChanges, setHasChanges] = useState(false);

    const handleDragEnd = (result) => {
      if (!result.destination) return;

      const items = Array.from(reorderingClasses);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      const updatedItems = items.map((item, index) => ({
        ...item,
        ordre: index + 1
      }));

      setReorderingClasses(updatedItems);
      setHasChanges(true);
    };

    const handleSave = async () => {
      try {
        await onReorder(sectionId, reorderingClasses);
        setHasChanges(false);
      } catch (error) {
        addToast(getErrorMessage(error, 'Erreur lors de la réorganisation'), 'error');
      }
    };

    return (
      <div className="p-6 border-2 border-dashed" style={{ borderColor: colors.secondary + '40' }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h4 className="text-lg font-semibold" style={{ color: colors.primary }}>
              Mode réoorganisation
            </h4>
            <p className="text-sm text-gray-600">
              Glissez-déposez pour réorganiser l'ordre des classes
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              Annuler
            </button>
            {droits.reordonner && (
              <button
                onClick={handleSave}
                disabled={!hasChanges}
                className="px-6 py-2 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
              >
                Sauvegarder
              </button>
            )}
          </div>
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="classes">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                {reorderingClasses.map((classe, index) => (
                  <Draggable key={classe.id} draggableId={classe.id.toString()} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`p-4 bg-white border rounded-lg transition-all ${
                          snapshot.isDragging
                            ? 'shadow-lg border-2'
                            : 'shadow-sm hover:shadow-md'
                        }`}
                        style={{
                          ...provided.draggableProps.style,
                          borderColor: snapshot.isDragging ? colors.primary : '#e5e7eb'
                        }}
                      >
                        <div className="flex items-center space-x-4">
                          <GripVertical className="w-5 h-5 text-gray-400" />
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                            style={{ backgroundColor: colors.primary }}
                          >
                            {classe.ordre}
                          </div>
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900">{classe.nom}</h5>
                            <p className="text-sm text-gray-600">
                              {classe.effectif || 0} élève(s) {formatPrice(classe.frais_mensuel)}/mois
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  });

  // ============================================================================
// COMPOSANT : MODALE D'ÉDITION CLASSE
// ============================================================================

  // Fenêtre « Modifier la classe », sur les primitives Dialog (convention des
  // modals KLASYS, DESIGN-SYSTEM.md, refaite le 01/11/2026) : format large en
  // deux colonnes plutôt qu'une longue colonne, pastille d'en-tête, en-tête et
  // pied fixes, boutons de pied de même taille. Les frais s'affichent en FCFA
  // entiers (le serveur les arrondit déjà) et la molette ne change aucun
  // montant. Sur Radix, la demande de taux s'ouvre PAR-DESSUS cette fenêtre :
  // Radix empile les deux.
  const EditModal = useComposantStable(function EditModal({ classe, onClose, onUpdate, onDemanderTaux }) {
    const [formData, setFormData] = useState({
      nom: classe?.nom || '',
      frais_inscription: montantEntier(classe?.frais_inscription),
      frais_reinscription: montantEntier(classe?.frais_reinscription),
      frais_mensuel: montantEntier(classe?.frais_mensuel),
      frais_transport: montantEntier(classe?.frais_transport),
      frais_cantine: montantEntier(classe?.frais_cantine),
      // Pas de taux horaire : il change par demande validée (ModalTauxHoraireClasse).
      seuil_eleves: classe?.seuil_eleves || '',
      sans_matiere: isClasseSansMatiere(classe)
    });

    if (!classe) return null;

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.nom.trim()) {
        addToast('Le nom de la classe est requis', 'error');
        return;
      }

      try {
        const result = await onUpdate(classe.id, formData);
        if (result?.success) {
          onClose();
        }
      } catch (error) {
        addToast(getErrorMessage(error, 'Erreur lors de la modification'), 'error');
      }
    };

    const champFrais = (cle, libelle) => (
      <div key={cle}>
        <label htmlFor={`classe-${cle}`} className="mb-1 block text-sm text-gray-700">{libelle}</label>
        <div className="relative">
          <input
            id={`classe-${cle}`}
            type="number"
            inputMode="numeric"
            min="0"
            step="1"
            value={formData[cle]}
            onChange={(e) => setFormData(prev => ({ ...prev, [cle]: e.target.value }))}
            onWheel={(e) => e.currentTarget.blur()}
            className={`${CHAMP_FENETRE} pr-14`}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
        </div>
      </div>
    );

    const taux = Number(classe?.taux_horaire) || 0;

    return (
      <Dialog open onOpenChange={(ouvert) => { if (!ouvert && !loading) onClose(); }}>
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
                <Pencil className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-lg font-semibold text-gray-900">Modifier la classe</DialogTitle>
                <DialogDescription className="text-sm text-gray-500">
                  {classe.nom}{classe.section_nom ? ` - ${classe.section_nom}` : ''}
                </DialogDescription>
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="grid gap-5 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
                <section className="space-y-4">
                  <div>
                    <label htmlFor="classe-nom" className="mb-1 block text-sm text-gray-700">Nom de la classe</label>
                    <input
                      id="classe-nom"
                      type="text"
                      value={formData.nom}
                      onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                      className={CHAMP_FENETRE}
                      required
                    />
                  </div>

                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Frais de la classe</p>
                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                      {champFrais('frais_inscription', "Frais d'inscription")}
                      {champFrais('frais_reinscription', 'Frais de réinscription')}
                      {champFrais('frais_mensuel', 'Frais mensuels')}
                      {champFrais('frais_transport', 'Frais de transport')}
                      {champFrais('frais_cantine', 'Frais de cantine')}
                    </div>
                  </div>
                </section>

                <section className="space-y-4">
                  {/* Le taux fait la paie des enseignants payés à l'heure de la classe : il
                      ne change que par une demande validée au Centre de Support, avec un
                      mois d'effet (décision de Daniel du 01/11/2026). Ici, en lecture. */}
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs text-gray-500">Taux horaire</p>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {taux > 0 ? `${formaterFCFA(taux)} de l'heure` : 'Non défini'}
                    </p>
                    <p className="mt-1 text-[11px] text-gray-500">
                      Il fait la paie des enseignants payés à l'heure de la classe, et change après validation au Centre de Support.
                    </p>
                    {droits.modifier && onDemanderTaux && (
                      <button
                        type="button"
                        onClick={() => onDemanderTaux(classe)}
                        className="mt-3 inline-flex h-9 items-center justify-center rounded-lg border border-[#246990]/30 bg-white px-4 text-sm font-medium text-[#246990] transition-colors hover:bg-[#eef7fb]"
                      >
                        Demander un nouveau taux
                      </button>
                    )}
                  </div>

                  <div>
                    <label htmlFor="classe-seuil" className="mb-1 block text-sm text-gray-700">Seuil d'élèves par classe</label>
                    {/* Sans le droit des seuils, la valeur part telle qu'elle a été lue :
                        le serveur ne demande ce droit que si le seuil change. */}
                    <input
                      id="classe-seuil"
                      type="number"
                      inputMode="numeric"
                      min="1"
                      step="1"
                      value={formData.seuil_eleves}
                      onChange={(e) => setFormData(prev => ({ ...prev, seuil_eleves: e.target.value }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      readOnly={!droits.seuils}
                      className={`${CHAMP_FENETRE}${droits.seuils ? '' : ' cursor-not-allowed bg-gray-100 text-gray-500'}`}
                      placeholder="Seuil global par défaut"
                    />
                    <p className={droits.seuils ? 'mt-1 text-[11px] text-gray-500' : 'mt-1 text-[11px] text-amber-700'}>
                      {droits.seuils ? 'Laissez vide pour utiliser le seuil global par défaut.' : AIDE_SEUIL_MODIFICATION}
                    </p>
                  </div>

                  {/* La case de KLASYS (ui/CaseACocher, DESIGN-SYSTEM.md). */}
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <CaseACocher
                      checked={formData.sans_matiere}
                      onChange={(coche) => setFormData(prev => ({ ...prev, sans_matiere: coche }))}
                      disabled={!droits.associer}
                      label="Classe sans matière"
                      description="Pour les classes comme garderie ou activités sans enseignements académiques."
                      aide={droits.associer ? null : AIDE_SANS_MATIERE}
                    />
                  </div>
                </section>
              </div>
            </div>

            <DialogFooter className="gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0">
              <button type="button" onClick={onClose} disabled={loading} className={BOUTON_PIED_SECONDAIRE}>
                Annuler
              </button>
              <button type="submit" disabled={loading} className={BOUTON_PIED_PRINCIPAL}>
                {loading && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}
                {loading ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  });

  // ============================================================================
// COMPOSANT : DASHBOARD PRINCIPAL
// ============================================================================

const Dashboard = useComposantStable(function Dashboard({ sections, onCreateClass, onManageSection, onViewClass, onAssignSubjects, onChooseClassAction }) {
  const allClasses = Object.values(classesBySection).flat();
  const mainClasses = allClasses.filter(classe => !classe.classe_mere_id);
  const annexClasses = allClasses.filter(classe => classe.classe_mere_id);
  const totalEleves = mainClasses.reduce((sum, c) => sum + (parseInt(c.effectif || c.effectif_actuel || 0, 10) || 0), 0);
  const sectionsWithClasses = sections.filter(section => {
    const sectionClasses = classesBySection[section.id] || [];
    return sectionClasses.some(classe => !classe.classe_mere_id);
  }).length;
  const sectionsToConfigure = Math.max(sections.length - sectionsWithClasses, 0);
  const assignedClassIds = new Set(
    matieres.flatMap(matiere => String(matiere.classes_ids || '')
      .split(',')
      .map(id => parseInt(id, 10))
      .filter(id => Number.isFinite(id)))
  );
  const sectionsWithoutClasses = sections.filter(section => {
    const sectionClasses = classesBySection[section.id] || [];
    return !sectionClasses.some(classe => !classe.classe_mere_id);
  });
  const classesWithoutMatieres = mainClasses.filter(classe => !isClasseSansMatiere(classe) && !assignedClassIds.has(parseInt(classe.id, 10)));
  const getEffectif = (classe) => parseInt(classe.effectif || classe.effectif_actuel || 0, 10) || 0;
  const getSeuil = (classe) => parseInt(classe.seuil_eleves || seuilGlobal || 0, 10) || null;
  // Annexes comprises (décision du 30/10/2026) : une annexe a son seuil, comme
  // une classe principale. Même règle que les alertes critiques « Classes au
  // seuil » et « Classes presque pleines », qui lisent, elles, l'année active
  // et les seuls élèves de l'année de la classe.
  const classesAtSeuil = allClasses.filter(classe => {
    const seuil = getSeuil(classe);
    return seuil && getEffectif(classe) >= seuil;
  });
  const classesNearSeuil = allClasses.filter(classe => {
    const seuil = getSeuil(classe);
    const effectif = getEffectif(classe);
    return seuil && effectif < seuil && effectif / seuil >= 0.8;
  });
  const todoItems = [
    {
      key: 'sections',
      title: 'Sections sans classe',
      count: sectionsWithoutClasses.length,
      detail: sectionsWithoutClasses.slice(0, 3).map(section => section.nom).join(', '),
      action: () => sectionsWithoutClasses[0] && onManageSection(sectionsWithoutClasses[0].id),
      actionLabel: 'Configurer',
      color: 'border-amber-200 bg-amber-50 text-amber-800'
    },
    {
      key: 'matieres',
      title: 'Classes sans matière',
      count: classesWithoutMatieres.length,
      detail: classesWithoutMatieres.slice(0, 3).map(classe => classe.nom).join(', '),
      // Sans le droit d'associer, la carte ouvre la classe en consultation.
      action: () => onChooseClassAction?.(droits.associer ? {
        title: 'Choisir une classe sans matière',
        description: 'Sélectionnez la classe à traiter avant d’associer les matières.',
        items: classesWithoutMatieres,
        actionLabel: 'Associer',
        variant: 'matieres',
        onSelect: (classe) => onAssignSubjects?.(classe.id)
      } : {
        title: 'Choisir une classe sans matière',
        description: 'Sélectionnez la classe à consulter.',
        items: classesWithoutMatieres,
        actionLabel: 'Ouvrir',
        variant: 'matieres',
        onSelect: (classe) => onViewClass(classe.id)
      }),
      actionLabel: 'Choisir',
      color: 'border-blue-200 bg-blue-50 text-blue-800'
    },
    {
      key: 'seuils',
      title: 'Seuil atteint',
      count: classesAtSeuil.length,
      detail: classesAtSeuil.slice(0, 3).map(classe => classe.nom).join(', '),
      action: () => onChooseClassAction?.({
        title: 'Classes avec seuil atteint',
        description: 'Sélectionnez la classe à ouvrir pour ajuster le seuil ou créer une annexe.',
        items: classesAtSeuil,
        actionLabel: 'Ouvrir',
        variant: 'seuil',
        onSelect: (classe) => onViewClass(classe.id)
      }),
      actionLabel: 'Choisir',
      color: 'border-red-200 bg-red-50 text-red-800'
    },
    {
      key: 'near-seuils',
      title: 'Classes presque pleines',
      count: classesNearSeuil.length,
      detail: classesNearSeuil.slice(0, 3).map(classe => classe.nom).join(', '),
      action: () => onChooseClassAction?.({
        title: 'Classes presque pleines',
        description: 'Sélectionnez la classe à contrôler.',
        items: classesNearSeuil,
        actionLabel: 'Contrôler',
        variant: 'seuil',
        onSelect: (classe) => onViewClass(classe.id)
      }),
      actionLabel: 'Choisir',
      color: 'border-orange-200 bg-orange-50 text-orange-800'
    }
  ].filter(item => item.count > 0);

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-[#246990] to-[#3084b5] rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">Classes principales</p>
              <p className="text-3xl font-bold">{mainClasses.length}</p>
            </div>
            <BookOpen className="w-12 h-12 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#319190] to-[#4aa3a3] rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100">Élèves actifs</p>
              <p className="text-3xl font-bold">{totalEleves}</p>
            </div>
            <Users className="w-12 h-12 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#c66d59] to-[#d4867a] rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100">Classes annexes</p>
              <p className="text-3xl font-bold">{annexClasses.length}</p>
            </div>
            <ChevronRight className="w-12 h-12 text-orange-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-100">Sections à configurer</p>
              <p className="text-3xl font-bold">{sectionsToConfigure}</p>
            </div>
            <Settings className="w-12 h-12 text-gray-200" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">À traiter</h3>
            <p className="text-sm text-gray-500">Points opérationnels à vérifier avant d'utiliser les classes au quotidien.</p>
          </div>
          {todoItems.length === 0 && (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
              Structure à jour
            </span>
          )}
        </div>
        {todoItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 p-6">
            {todoItems.map(item => (
              <div key={item.key} className={`border rounded-lg p-4 ${item.color}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold">{item.title}</p>
                    <p className="text-2xl font-bold mt-1">{item.count}</p>
                  </div>
                  <button
                    type="button"
                    onClick={item.action}
                    className="px-3 py-1 rounded-md bg-white/80 text-xs font-medium hover:bg-white transition-colors"
                  >
                    {item.actionLabel}
                  </button>
                </div>
                <p className="text-xs mt-3 min-h-[32px]">
                  {item.detail || 'Aucun détail disponible'}{item.count > 3 ? '...' : ''}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-sm text-gray-600">
            Toutes les sections ont au moins une classe, les classes principales ont des matières et aucun seuil n'est atteint.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sectionsStables.map(section => {
          const stats = calculateSectionStats(section.id);
          const colors = getSectionColors(section.id);

          return (
            <div
              key={section.id}
              className="shadow-sm cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 rounded-xl overflow-hidden border"
              style={{
                borderColor: colors.primary,
                backgroundColor: colors.light
              }}
              onClick={() => onManageSection(section.id)}
            >
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                    style={{
                      backgroundColor: colors.primary
                    }}
                  >
                    {getSectionIcon(section, 'w-5 h-5')}
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-green-600">Actif</span>
                  </div>
                </div>

                <h3 className="font-bold mb-1 text-sm text-gray-900">
                  {section.nom}
                </h3>
                <p className="text-xs mb-3 text-gray-600">
                  Gestion des classes de {section.nom.toLowerCase()}
                </p>

                <div className="space-y-1 text-xs mb-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Classes</span>
                    <span
                      className="font-semibold"
                      style={{ color: colors.primary }}
                    >
                      {stats.total_classes}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Élèves</span>
                    <span
                      className="font-semibold"
                      style={{ color: colors.primary }}
                    >
                      {stats.total_eleves}
                    </span>
                  </div>
                </div>

                <button
                  className="w-full text-xs font-medium py-2 px-4 rounded-lg transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-800"
                >
                  Gérer
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

  const getClasseCapacity = (classe) => {
    const effectif = parseInt(classe?.effectif_actuel || classe?.effectif || 0, 10) || 0;
    const seuil = parseInt(classe?.seuil_effectif || classe?.seuil_eleves || seuilGlobal || 0, 10) || 0;
    const ratio = seuil > 0 ? effectif / seuil : 0;
    const percent = seuil > 0 ? Math.min(Math.round(ratio * 100), 100) : 0;
    const remaining = seuil > 0 ? Math.max(seuil - effectif, 0) : null;
    const tone = seuil > 0 && effectif >= seuil ? 'red' : ratio >= 0.8 ? 'amber' : 'emerald';
    return { effectif, seuil, ratio, percent, remaining, tone };
  };

  const getCapacityToneClasses = (tone) => {
    if (tone === 'red') return { text: 'text-red-700', bg: 'bg-red-500', soft: 'bg-red-50 border-red-200', label: 'Seuil atteint' };
    if (tone === 'amber') return { text: 'text-amber-700', bg: 'bg-amber-500', soft: 'bg-amber-50 border-amber-200', label: 'Surveillance' };
    return { text: 'text-emerald-700', bg: 'bg-emerald-500', soft: 'bg-emerald-50 border-emerald-200', label: 'Disponible' };
  };

  const ProgressBar = useComposantStable(function ProgressBar({ percent, tone = 'emerald' }) {
    const toneClasses = getCapacityToneClasses(tone);
    return (
      <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
        <div
          className={'h-full rounded-full transition-all ' + toneClasses.bg}
          style={{ width: percent + '%' }}
        />
      </div>
    );
  });

  const InfoRow = useComposantStable(({ label, value }) => (
    <div className="flex items-center justify-between gap-4 border-b border-gray-100 py-3 last:border-b-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-semibold text-gray-900 text-right">{value}</span>
    </div>
  ));

  const ClasseDetailsContent = useComposantStable(function ClasseDetailsContent({ data, activeTab, onTabChange, onReload, onManageMatieres, onToggleSansMatiere }) {

    const tabs = [
      { id: 'infos', label: 'Informations', icon: Info },
      { id: 'frais', label: 'Frais', icon: Wallet },
      { id: 'matieres', label: 'Matières', icon: BookOpen },
      { id: 'annexes', label: 'Annexes', icon: Layers },
      { id: 'seuils', label: 'Seuils', icon: Gauge }
    ];

    return (
      <div className="bg-white">
        <div className="border-b bg-gray-50/80 px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => onTabChange(tab.id)}
                  className={(isActive
                    ? 'bg-[#246990] text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-[#246990]/30 hover:text-[#246990]') + ' inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors'}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'infos' && <InfosTab data={data} />}
          {activeTab === 'frais' && <FraisTab data={data} />}
          {activeTab === 'matieres' && (
            <MatieresTab
              data={data}
              onManageMatieres={onManageMatieres}
              onToggleSansMatiere={onToggleSansMatiere}
            />
          )}
          {activeTab === 'annexes' && <AnnexesTab data={data} onReload={onReload} />}
          {activeTab === 'seuils' && <SeuilsTab data={data} onReload={onReload} />}
        </div>
      </div>
    );
  });

  const InfosTab = useComposantStable(function InfosTab({ data }) {
    const { classe, statistiques } = data;
    const nombreClasses = statistiques.nombre_classes || 0;
    const annexesCount = Math.max(nombreClasses - 1, 0);
    const placesGroupe = statistiques.places_disponibles ?? 0;
    const isAnnexe = Boolean(classe.classe_mere_id) && !classe.est_classe_principale;
    const hasAnnexes = nombreClasses > 1;

    return (
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 rounded-lg border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
            <div className="rounded-lg bg-[#246990]/10 p-2 text-[#246990]">
              <Info className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Identité de la classe</h4>
              <p className="text-sm text-gray-500">Référence, section et position d'affichage dans la progression.</p>
            </div>
          </div>
          <div className="mt-2">
            <InfoRow label="Nom" value={classe.nom} />
            <InfoRow label="Section" value={classe.section_nom || 'Non renseignée'} />
            <InfoRow label="Type" value={isAnnexe ? 'Classe annexe' : 'Classe principale'} />
            <InfoRow label="Position dans la section" value={classe.ordre ? 'N° ' + classe.ordre : 'Non définie'} />
            <div className="mt-3 rounded-lg border border-[#246990]/10 bg-[#246990]/5 px-4 py-3 text-xs leading-5 text-gray-600">
              Cette position détermine l'ordre d'affichage et la progression des classes dans cette section.
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-[#246990]/15 bg-[#246990]/5 p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="font-semibold text-gray-900">Organisation du groupe</h4>
              <p className="mt-1 text-sm text-gray-500">Annexes et capacité partagée, sans répéter les indicateurs du résumé.</p>
            </div>
            <Layers className="w-6 h-6 text-[#246990]" />
          </div>

          <div className="mt-5 space-y-3">
            <div className="flex items-center justify-between rounded-lg bg-white px-4 py-3 border border-[#246990]/10">
              <div className="flex items-center gap-3">
                <Layers className="w-4 h-4 text-[#246990]" />
                <span className="text-sm text-gray-600">Annexes rattachées</span>
              </div>
              <span className="font-semibold text-gray-950">{annexesCount}</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-white px-4 py-3 border border-[#246990]/10">
              <div className="flex items-center gap-3">
                <ListChecks className="w-4 h-4 text-[#246990]" />
                <span className="text-sm text-gray-600">Mode de suivi</span>
              </div>
              <span className="font-semibold text-gray-950">{hasAnnexes ? 'Groupe' : 'Classe seule'}</span>
            </div>
            {hasAnnexes && (
              <div className="flex items-center justify-between rounded-lg bg-white px-4 py-3 border border-[#246990]/10">
                <div className="flex items-center gap-3">
                  <Gauge className="w-4 h-4 text-[#246990]" />
                  <span className="text-sm text-gray-600">Places disponibles groupe</span>
                </div>
                <span className="font-semibold text-gray-950">{placesGroupe}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  });

  const FraisTab = useComposantStable(function FraisTab({ data }) {
    const { classe } = data;
    const fraisEntree = [
      { label: "Inscription", value: classe.frais_inscription, icon: ListChecks },
      { label: "Réinscription", value: classe.frais_reinscription, icon: RefreshCw }
    ];
    const services = [
      { label: "Transport", value: classe.frais_transport, icon: Bus, tone: 'text-amber-700 bg-amber-50 border-amber-200' },
      { label: "Cantine", value: classe.frais_cantine, icon: Utensils, tone: 'text-rose-700 bg-rose-50 border-rose-200' }
    ];

    return (
      <div className="space-y-5">
        <div className="overflow-hidden rounded-xl border border-[#246990]/15 bg-white shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1.35fr]">
            <div className="bg-[#246990] p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-white/15 p-2 ring-1 ring-white/20">
                  <Wallet className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white/75">Mensualité de référence</p>
                  <h4 className="mt-1 text-3xl font-bold">{formatPrice(classe.frais_mensuel)}</h4>
                </div>
              </div>
              <p className="mt-5 text-sm leading-6 text-white/80">
                Montant mensuel appliqué aux élèves de cette classe avant les réductions et cas particuliers.
              </p>
            </div>

            <div className="p-6">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h4 className="font-semibold text-gray-900">Frais d'entrée</h4>
                  <p className="text-sm text-gray-500">Montants facturés au début du parcours.</p>
                </div>
                <ListChecks className="w-5 h-5 text-[#246990]" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {fraisEntree.map(item => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="rounded-lg border border-gray-200 bg-gray-50/70 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium text-gray-600">{item.label}</span>
                        <Icon className="w-4 h-4 text-[#246990]" />
                      </div>
                      <p className="mt-3 text-xl font-bold text-gray-950">{formatPrice(item.value)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="rounded-xl border border-gray-200 bg-white p-5 lg:col-span-2 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h4 className="font-semibold text-gray-900">Services facturables</h4>
                <p className="text-sm text-gray-500">Options associées à la classe.</p>
              </div>
              <Bus className="w-5 h-5 text-gray-500" />
            </div>
            <div className="space-y-3">
              {services.map(item => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className={'flex items-center justify-between gap-4 rounded-lg border px-4 py-3 ' + item.tone}>
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-white/70 p-2">
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                    <span className="text-base font-bold text-gray-950">{formatPrice(item.value)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h4 className="font-semibold text-gray-900">Taux horaire</h4>
                <p className="mt-1 text-sm text-indigo-700">Base de calcul enseignant horaire.</p>
              </div>
              <Clock className="w-5 h-5 text-indigo-700" />
            </div>
            <p className="mt-6 text-2xl font-bold text-gray-950">
              {classe.taux_horaire ? formatPrice(classe.taux_horaire) + '/h' : 'Non défini'}
            </p>
          </div>
        </div>
      </div>
    );
  });

  const MatieresTab = useComposantStable(function MatieresTab({ data, onManageMatieres, onToggleSansMatiere }) {
    const { classe, matieres = [] } = data;
    const classeSansMatiere = isClasseSansMatiere(classe);
    const hasMatieres = matieres.length > 0;

    const StatusPanel = () => (
      <div className={(classeSansMatiere
        ? 'border-[#246990]/15 bg-[#246990]/5'
        : 'border-gray-200 bg-white') + ' rounded-xl border p-5 shadow-sm'}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={(classeSansMatiere ? 'bg-white text-[#246990]' : 'bg-[#246990]/10 text-[#246990]') + ' rounded-lg p-3 ring-1 ring-[#246990]/10'}>
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">
                {classeSansMatiere ? 'Matières non requises' : 'Matières requises'}
              </h4>
              <p className="mt-1 text-sm text-gray-600">
                {classeSansMatiere
                  ? 'Cette classe est exclue des alertes de classes sans matière.'
                  : 'Cette classe doit avoir des matières associées, sauf si elle est paramétrée sans matière.'}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:items-end gap-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Classe sans matière</span>
              {onToggleSansMatiere && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={classeSansMatiere}
                  onClick={() => onToggleSansMatiere(classe, !classeSansMatiere, matieres)}
                  disabled={loading}
                  className={(classeSansMatiere ? 'bg-[#246990]' : 'bg-gray-300') + ' relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#246990]/30 disabled:cursor-not-allowed disabled:opacity-60'}
                  title={classeSansMatiere ? 'Désactiver classe sans matière' : 'Activer classe sans matière'}
                >
                  <span
                    className={(classeSansMatiere ? 'translate-x-6' : 'translate-x-1') + ' inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform'}
                  />
                </button>
              )}
              <span className={(classeSansMatiere ? 'text-[#246990]' : 'text-gray-500') + ' w-20 text-sm font-semibold'}>
                {classeSansMatiere ? 'Activé' : 'Désactivé'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );

    const bandeauAssociations = bandeau(texteBandeauAssociationsClasse(droits));

    if (!hasMatieres) {
      return (
        <div className="space-y-4">
          {bandeauAssociations}
          {StatusPanel()}
          {!classeSansMatiere && (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/70 px-6 py-12 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Aucune matière</h4>
              <p className="text-gray-600">Aucune matière n'est encore associée à cette classe.</p>
              {onManageMatieres && (
                <button
                  type="button"
                  onClick={onManageMatieres}
                  className="mt-6 inline-flex items-center px-5 py-2.5 bg-[#246990] text-white rounded-lg hover:bg-[#1f5a7d] transition-colors"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Associer des matières
                </button>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {bandeauAssociations}
        {StatusPanel()}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">Matières associées</h4>
            <p className="text-sm text-gray-500">{matieres.length} matière{matieres.length > 1 ? 's' : ''} configurée{matieres.length > 1 ? 's' : ''} pour cette classe.</p>
          </div>
          {onManageMatieres && (
            <button
              type="button"
              onClick={onManageMatieres}
              className="inline-flex items-center justify-center px-4 py-2 border border-[#246990] text-[#246990] rounded-lg hover:bg-[#246990]/5 transition-colors"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Gérer les matières
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {matieres.map(matiere => (
            <div key={matiere.id} className="rounded-lg border border-gray-200 bg-white p-4 hover:border-[#246990]/30 hover:shadow-sm transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h5 className="font-semibold text-gray-900 truncate">{matiere.nom}</h5>
                  <p className="mt-1 text-xs text-gray-500">Code {matiere.code}</p>
                </div>
                <span className="rounded-full bg-[#246990]/10 px-2 py-1 text-xs font-semibold text-[#246990]">
                  Coef. {matiere.coefficient_effectif ?? matiere.coefficient}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  });

  const AnnexesTab = useComposantStable(function AnnexesTab({ data, onReload }) {
    const { classes_annexes } = data;
    const [creatingAnnexe, setCreatingAnnexe] = useState(false);
    const [deletingAnnexeId, setDeletingAnnexeId] = useState(null);

    const createAnnexe = async (classePrincipaleId) => {
      try {
        setCreatingAnnexe(true);
        addToast('Creation de la classe annexe en cours...', 'loading');
        const result = await api.classes.createAnnexe(classePrincipaleId);

        if (result.success) {
          addToast('Classe annexe creee avec succes', 'success');
          await onReload?.();
        } else {
          addToast(messageEchec(result, 'Erreur lors de la creation'), 'error');
        }
      } catch (error) {
        addToast(getErrorMessage(error, 'Erreur lors de la creation de la classe annexe'), 'error');
      } finally {
        setCreatingAnnexe(false);
      }
    };

    const handleCreateAnnexe = () => {
      if (creatingAnnexe) return;
      const classePrincipale = classes_annexes.find(c => c.est_classe_principale) || data.classe;
      setConfirmDialog({
        isOpen: true,
        title: 'Creer une classe annexe',
        description: 'Une nouvelle classe annexe sera creee avec les memes frais, le meme seuil et les matieres de la classe principale. Continuer ?',
        confirmText: 'Creer annexe',
        cancelText: 'Annuler',
        type: 'question',
        onConfirm: async () => {
          closeConfirmDialog();
          await createAnnexe(classePrincipale.id);
        }
      });
    };

    const handleDeleteAnnexe = (classe) => {
      setConfirmDialog({
        isOpen: true,
        title: 'Supprimer la classe annexe',
        description: 'Voulez-vous supprimer la classe annexe "' + classe.nom + '" ? La suppression sera refusee si elle contient des eleves ou des liaisons actives.',
        confirmText: 'Supprimer',
        cancelText: 'Annuler',
        type: 'error',
        onConfirm: async () => {
          closeConfirmDialog();
          try {
            setDeletingAnnexeId(classe.id);
            addToast('Suppression de la classe annexe en cours...', 'loading');
            const result = await api.classes.delete(classe.id);
            if (result.success) {
              addToast('Classe annexe supprimee avec succes', 'success');
              await onReload?.();
            } else {
              addToast(messageEchec(result, 'Erreur lors de la suppression'), 'error');
            }
          } catch (error) {
            addToast(getErrorMessage(error, 'Erreur lors de la suppression de la classe annexe'), 'error');
          } finally {
            setDeletingAnnexeId(null);
          }
        }
      });
    };

    return (
      <div className="space-y-5">
        {bandeau(texteBandeauAnnexes(droits))}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">Groupe de classes</h4>
            <p className="text-sm text-gray-500">{classes_annexes.length} classe{classes_annexes.length > 1 ? 's' : ''} dans ce groupe.</p>
          </div>
          {droits.creer && (
            <button
              onClick={handleCreateAnnexe}
              disabled={creatingAnnexe}
              className="inline-flex items-center justify-center px-4 py-2 bg-[#246990] hover:bg-[#1f5a7d] text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {creatingAnnexe ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Creation...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Creer classe annexe
                </>
              )}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {classes_annexes.map(classe => {
            const capacity = getClasseCapacity({ ...classe, seuil_effectif: classe.seuil_eleves || data.classe.seuil_effectif });
            const toneClasses = getCapacityToneClasses(capacity.tone);
            const deleting = deletingAnnexeId === classe.id;
            return (
              <div key={classe.id} className="rounded-lg border border-gray-200 bg-white p-5 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h5 className="font-semibold text-gray-900">{classe.nom}</h5>
                    <p className="mt-1 text-xs text-gray-500">Prefixe {classe.prefixe || 'A'}</p>
                  </div>
                  {classe.est_classe_principale ? (
                    <span className="rounded-full bg-[#246990]/10 px-2 py-1 text-xs font-semibold text-[#246990]">
                      Principale
                    </span>
                  ) : droits.supprimer && (
                    <button
                      type="button"
                      onClick={() => handleDeleteAnnexe(classe)}
                      disabled={deleting}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-100 text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
                      title="Supprimer la classe annexe"
                      aria-label="Supprimer la classe annexe"
                    >
                      {deleting ? (
                        <div className="h-4 w-4 rounded-full border-2 border-red-500 border-t-transparent animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>

                <div className="mt-5 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Effectif</span>
                    <span className="font-semibold text-gray-900">{capacity.effectif} / {capacity.seuil || '-'}</span>
                  </div>
                  <ProgressBar percent={capacity.percent} tone={capacity.tone} />
                  <p className={'text-xs font-medium ' + toneClasses.text}>{toneClasses.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  });

  const SeuilsTab = useComposantStable(function SeuilsTab({ data, onReload }) {
    const { classe } = data;
    const [seuilLocal, setSeuilLocal] = useState(classe.seuil_eleves || '');
    const [saving, setSaving] = useState(false);
    const capacity = getClasseCapacity(classe);
    const toneClasses = getCapacityToneClasses(capacity.tone);

    const handleUpdateSeuil = async () => {
      if (!droits.seuils) return;
      try {
        setSaving(true);
        addToast('Mise à jour du seuil en cours...', 'loading');
        const seuil = seuilLocal ? parseInt(seuilLocal, 10) : null;
        const result = await api.classes.updateSeuil(classe.id, seuil);
        if (result.success) {
          addToast('Seuil mis à jour avec succès', 'success');
          await onReload?.();
        } else {
          addToast(messageEchec(result, 'Erreur lors de la mise à jour'), 'error');
        }
      } catch (error) {
        addToast(getErrorMessage(error, 'Erreur lors de la mise à jour du seuil'), 'error');
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className={'rounded-lg border p-5 lg:col-span-1 ' + toneClasses.soft}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="font-semibold text-gray-900">Seuil actuel</h4>
              <p className={'mt-1 text-sm font-medium ' + toneClasses.text}>{toneClasses.label}</p>
            </div>
            <Gauge className={'w-6 h-6 ' + toneClasses.text} />
          </div>
          <div className="mt-6">
            <div className="mb-2 flex justify-between text-sm">
              <span className="text-gray-600">{capacity.effectif} élève(s)</span>
              <span className="font-semibold text-gray-900">{capacity.seuil || '-'} max</span>
            </div>
            <ProgressBar percent={capacity.percent} tone={capacity.tone} />
            <p className="mt-3 text-xs text-gray-600">
              Source : {classe.seuil_eleves ? 'seuil personnalisé' : 'seuil global par défaut'}
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 lg:col-span-2">
          <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
            <div className="rounded-lg bg-[#246990]/10 p-2 text-[#246990]">
              <Gauge className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Configuration du seuil</h4>
              <p className="text-sm text-gray-500">Laissez vide pour revenir au seuil global.</p>
            </div>
          </div>

          <div className="mt-5 space-y-4">
            {bandeau(texteBandeauSeuil(droits))}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seuil personnalisé pour cette classe
              </label>
              <input
                type="number"
                value={seuilLocal}
                onChange={(e) => setSeuilLocal(e.target.value)}
                readOnly={!droits.seuils}
                placeholder="Laisser vide pour le seuil global"
                min="1"
                max="100"
                className={'w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#246990] focus:ring-1 focus:ring-[#246990] outline-none' + (droits.seuils ? '' : ' bg-gray-100 text-gray-500 cursor-not-allowed')}
              />
            </div>

            {droits.seuils && (
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setSeuilLocal('')}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Réinitialiser
                </button>
                <button
                  type="button"
                  onClick={handleUpdateSeuil}
                  disabled={saving}
                  className="px-6 py-2 bg-[#246990] hover:bg-[#1f5a7d] text-white rounded-lg transition-colors disabled:opacity-50 inline-flex items-center"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Sauvegarde...
                    </>
                  ) : (
                    'Sauvegarder'
                  )}
                </button>
              </div>
            )}
          </div>

          {capacity.seuil > 0 && capacity.effectif >= capacity.seuil && (
            <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex gap-3">
                <Gauge className="w-5 h-5 text-red-700 mt-0.5" />
                <div>
                  <h5 className="font-medium text-red-900">Seuil atteint</h5>
                  <p className="text-sm text-red-700">
                    {droits.creer && droits.seuils
                      ? 'Cette classe a atteint ou dépassé son seuil. Créez une classe annexe ou augmentez le seuil.'
                      : 'Cette classe a atteint ou dépassé son seuil. Une personne autorisée doit créer une classe annexe ou augmenter le seuil.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  });


  const ClassActionChoiceModal = useComposantStable(function ClassActionChoiceModal() {
    const { isOpen, title, description, items, actionLabel, variant, onSelect } = classChoiceModal;
    if (!isOpen) return null;

    const isSubjectVariant = variant === 'matieres';
    const HeaderIcon = isSubjectVariant ? BookOpen : Gauge;

    return (
      <div className={`${CONFIRM_DIALOG_OVERLAY_CLASS} flex items-center justify-center p-4`}>
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b bg-gray-50 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-[#246990]/10 p-2 text-[#246990]">
                <HeaderIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                <p className="text-sm text-gray-600 mt-1">{description}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeClassChoiceModal}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              title="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 overflow-y-auto flex-1 space-y-3">
            {items.map(classe => {
              const capacity = getClasseCapacity(classe);
              const toneClasses = getCapacityToneClasses(capacity.tone);
              return (
                <div key={classe.id} className="rounded-lg border border-gray-200 bg-white p-4 hover:border-[#246990]/30 hover:shadow-sm transition-all">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{classe.nom}</p>
                      <p className="mt-1 text-sm text-gray-500">{classe.section_nom || 'Section non renseignée'}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                        {isSubjectVariant ? (
                          <span className="rounded-full bg-blue-50 px-2 py-1 font-medium text-blue-700 border border-blue-200">Aucune matière associée</span>
                        ) : (
                          <span className={'rounded-full px-2 py-1 font-medium border ' + toneClasses.soft + ' ' + toneClasses.text}>
                            {capacity.effectif} / {capacity.seuil || '-'} élèves
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        closeClassChoiceModal();
                        onSelect?.(classe);
                      }}
                      className="inline-flex items-center justify-center rounded-lg bg-[#246990] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1f5a7d] transition-colors"
                    >
                      {actionLabel}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  });

  const ClasseMatieresAssociationModal = useComposantStable(function ClasseMatieresAssociationModal() {
    const { isOpen, classe, initialMatiereIds, selectedMatiereIds, saving } = classeMatieresModal;
    if (!isOpen || !classe) return null;

    const initialSet = new Set(initialMatiereIds);
    const selectedSet = new Set(selectedMatiereIds);
    const toAdd = selectedMatiereIds.filter(id => !initialSet.has(id));
    const toRemove = initialMatiereIds.filter(id => !selectedSet.has(id));
    const hasChanges = toAdd.length > 0 || toRemove.length > 0;
    const sortedMatieres = [...matieres].sort((a, b) => String(a.nom || '').localeCompare(String(b.nom || '')));

    return (
      <div className={`${CONFIRM_DIALOG_OVERLAY_CLASS} flex items-center justify-center p-4`}>
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold text-[#246990]">Associer les matières</h3>
              <p className="text-sm text-gray-600 mt-1">{classe.nom} - {selectedMatiereIds.length} matière{selectedMatiereIds.length > 1 ? 's' : ''} sélectionnée{selectedMatiereIds.length > 1 ? 's' : ''}</p>
            </div>
            <button
              type="button"
              onClick={closeClasseMatieresModal}
              disabled={saving}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              title="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1">
            {sortedMatieres.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
                <BookOpen className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="font-medium text-gray-900">Aucune matière disponible</p>
                <p className="text-sm text-gray-500 mt-1">Créez d'abord des matières avant de les associer à cette classe.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {sortedMatieres.map(matiere => {
                  const matiereId = parseInt(matiere.id, 10);
                  const isSelected = selectedSet.has(matiereId);
                  const wasAssociated = initialSet.has(matiereId);
                  const willBeAdded = isSelected && !wasAssociated;
                  const willBeRemoved = !isSelected && wasAssociated;
                  const selectionClass = isSelected
                    ? (willBeAdded ? 'border-green-300 bg-green-50' : 'border-[#246990] bg-[#246990]/5')
                    : (willBeRemoved ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50');

                  return (
                    <label
                      key={matiere.id}
                      className={'flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ' + selectionClass}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleMatiereForClasse(matiereId)}
                        disabled={saving}
                        className="mt-1 rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-medium text-gray-900 truncate">{matiere.nom}</p>
                          <span className="text-xs font-medium bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{matiere.code}</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Coefficient par défaut : {matiere.coefficient}</p>
                        <div className="mt-2 h-6">
                          {wasAssociated && isSelected && (
                            <span className="inline-flex text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">Déjà associée</span>
                          )}
                          {willBeAdded && (
                            <span className="inline-flex text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">À ajouter</span>
                          )}
                          {willBeRemoved && (
                            <span className="inline-flex text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">À retirer</span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-600">
              {hasChanges ? (
                <span>{toAdd.length} à ajouter, {toRemove.length} à retirer</span>
              ) : (
                <span>Aucune modification</span>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeClasseMatieresModal}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={saveClasseMatieresAssociations}
                disabled={!hasChanges || saving || sortedMatieres.length === 0}
                className="px-5 py-2 bg-[#246990] text-white rounded-lg hover:bg-[#1f5a7d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  });

  // ============================================================================
// RENDU PRINCIPAL
// ============================================================================


  const isInitialLoading = loadingParametres || chargementInitial;

  if (isInitialLoading) {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-4">
        <LoadingSpinner message="Chargement des classes..." color="#246990" fullHeight />
      </div>
    );
  }
  return (
    <div className="w-full min-h-screen bg-gray-50 p-4">
      {currentToast && (
        <Toast
          message={currentToast.message}
          type={currentToast.type}
          onClose={closeToast}
        />
      )}

      <ClassActionChoiceModal />
      <ClasseMatieresAssociationModal />

      {activeView === 'dashboard' && (
        <Dashboard
          sections={sectionsStables}
          onCreateClass={() => setActiveView('sections')}
          onManageSection={(sectionId) => {
            const section = sectionsStables.find(s => s.id === sectionId);
            if (section) {
              const newExpanded = new Set(expandedSections);
              newExpanded.add(sectionId);
              setExpandedSections(newExpanded);
              setActiveView('sections');
            }
          }}
          onViewClass={handleViewClasseDetails}
          onAssignSubjects={openClasseMatieresModal}
          onChooseClassAction={openClassChoiceModal}
        />
      )}

      {activeView === 'sections' && (
        <div className="space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <button
              onClick={() => setActiveView('dashboard')}
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:border-[#246990]/30 hover:text-[#246990] transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au dashboard
            </button>

            {droits.seuils ? (
              <button
                onClick={() => setShowSeuilGlobalModal(true)}
                className="inline-flex items-center justify-center rounded-lg bg-[#246990] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#1f5a7d] transition-colors"
                title="Configurer le seuil par défaut"
              >
                <Settings className="w-4 h-4 mr-2" />
                Seuil par défaut ({seuilGlobal})
              </button>
            ) : (
              <span className="text-sm text-gray-600">Seuil par défaut : {seuilGlobal} élèves</span>
            )}
          </div>

          {bandeau(texteBandeauSections(droits))}

          <div className="space-y-6">
            {sectionsStables.map(section =>  {
              const sectionClasses = classesBySection[section.id] || [];
              return (
                <SectionCard
                  key={section.id}
                  section={section}
                  classes={sectionClasses}
                />
              );
            })}
          </div>
        </div>
      )}


      {activeView === 'details' && classeDetailsData && (() => {
        const { classe, matieres: classeMatieres = [], statistiques = {} } = classeDetailsData;
        const capacity = getClasseCapacity(classe);
        const toneClasses = getCapacityToneClasses(capacity.tone);
        const sectionIcon = getSectionIcon({ nom: classe.section_nom }, 'w-7 h-7');
        return (
          <div className="space-y-6">
            <button
              onClick={() => {
                setClasseDetailsData(null);
                setSelectedClasseDetails(null);
                setActiveClasseDetailTab('infos');
                setActiveView('sections');
              }}
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:border-[#246990]/30 hover:text-[#246990] transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour aux classes
            </button>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="bg-gradient-to-r from-[#246990] via-[#2f7fa6] to-[#319190] px-6 py-6 text-white">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                  <div className="flex items-start gap-4 min-w-0">
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-white/15 ring-1 ring-white/20">
                      {sectionIcon}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white/75">Détail de classe</p>
                      <h2 className="mt-1 text-2xl font-bold tracking-normal">{classe.nom}</h2>
                      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/80">
                        <span>{classe.section_nom || 'Section non renseignée'}</span>
                        <span className="h-1 w-1 rounded-full bg-white/45" aria-hidden="true" />
                        <span>
                          {classe.ordre
                            ? `Position ${classe.ordre} dans la section, utilisée pour l'affichage et la progression.`
                            : 'Position non définie dans la section.'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {droits.modifier && (
                    <button
                      type="button"
                      onClick={() => setEditingClasse(classe)}
                      className="inline-flex items-center justify-center rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#246990] shadow-sm hover:bg-white/90 transition-colors"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Modifier
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 p-5 border-b border-gray-100 bg-gray-50/60">
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <p className="text-xs font-medium uppercase text-gray-500">Effectif</p>
                  <p className="mt-2 text-2xl font-bold text-gray-950">{capacity.effectif}</p>
                  <p className="mt-1 text-xs text-gray-500">Élèves actifs</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <p className="text-xs font-medium uppercase text-gray-500">Seuil</p>
                  <p className="mt-2 text-2xl font-bold text-gray-950">{capacity.seuil || '-'}</p>
                  <p className="mt-1 text-xs text-gray-500">{classe.seuil_eleves ? 'Personnalisé' : 'Global par défaut'}</p>
                </div>
                <div className={'rounded-lg border p-4 ' + toneClasses.soft}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500">Remplissage</p>
                      <p className="mt-2 text-2xl font-bold text-gray-950">{capacity.percent}%</p>
                    </div>
                    <Gauge className={'w-5 h-5 ' + toneClasses.text} />
                  </div>
                  <div className="mt-3">
                    <ProgressBar percent={capacity.percent} tone={capacity.tone} />
                  </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <p className="text-xs font-medium uppercase text-gray-500">Matières</p>
                  <p className="mt-2 text-2xl font-bold text-gray-950">{isClasseSansMatiere(classe) ? '-' : classeMatieres.length}</p>
                  <p className="mt-1 text-xs text-gray-500">{isClasseSansMatiere(classe) ? 'Non requises' : 'Associées à cette classe'}</p>
                </div>
              </div>

              <ClasseDetailsContent
                data={{ ...classeDetailsData, statistiques }}
                activeTab={activeClasseDetailTab}
                onTabChange={setActiveClasseDetailTab}
                onReload={() => reloadClasseDetails(selectedClasseDetails)}
                onManageMatieres={droits.associer ? () => openClasseMatieresModal(selectedClasseDetails, classeDetailsData) : undefined}
                onToggleSansMatiere={droits.associer ? requestClasseSansMatiere : undefined}
              />
            </div>
          </div>
        );
      })()}

      {activeView === 'creation' && selectedSection && (() => {
        const colors = getSectionColors(selectedSection.id);
        return (
          <div className="w-full space-y-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setActiveView('sections')}
                  className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Retour aux sections
                </button>
                <div className="text-gray-400">/</div>
                <h2 className="text-2xl font-bold" style={{ color: colors.primary }}>
                  Créer une nouvelle classe
                </h2>
              </div>
            </div>

            <div
              className="rounded-xl p-6 border-2"
              style={{
                backgroundColor: colors.light,
                borderColor: colors.secondary + '40'
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white shadow-sm"
                    style={{ backgroundColor: colors.primary }}
                  >
                    {getSectionIcon(selectedSection, 'w-6 h-6')}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold" style={{ color: colors.primary }}>
                      {selectedSection.nom}
                    </h3>
                    <p className="text-gray-600">
                      {creationData.progression_preview?.length || 0} classe(s) existante(s)
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Ordre suggéré</p>
                  <p className="text-2xl font-bold" style={{ color: colors.primary }}>
                    {creationData.ordre_suggere}
                  </p>
                </div>
              </div>
            </div>

            {creationData.progression_preview && creationData.progression_preview.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h4 className="font-semibold mb-4 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2" style={{ color: colors.primary }} />
                  Progression actuelle dans cette section
                </h4>
                <div className="flex items-center space-x-3 overflow-x-auto pb-2">
                  {creationData.progression_preview.map((classe, index) => (
                    <React.Fragment key={classe.id}>
                      <div className="flex flex-col items-center space-y-2 min-w-0">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm"
                          style={{ backgroundColor: colors.primary }}
                        >
                          {classe.ordre}
                        </div>
                        <div className="text-center min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate max-w-20">
                            {classe.nom}
                          </p>
                          <p className="text-xs text-gray-500">
                            {classe.effectif || 0} élèves
                          </p>
                        </div>
                      </div>
                      {index < creationData.progression_preview.length - 1 && (
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                    </React.Fragment>
                  ))}
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex flex-col items-center space-y-2 min-w-0">
                    <div
                      className="w-12 h-12 rounded-full border-2 border-dashed flex items-center justify-center font-bold text-sm"
                      style={{
                        borderColor: colors.primary,
                        color: colors.primary,
                        backgroundColor: colors.light
                      }}
                    >
                      {creationData.ordre_suggere}
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium" style={{ color: colors.primary }}>
                        Nouvelle
                      </p>
                      <p className="text-xs text-gray-500">À créer</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div
                className="px-6 py-4 border-b"
                style={{ backgroundColor: colors.light }}
              >
                <h3 className="text-lg font-semibold" style={{ color: colors.primary }}>
                  Informations de la classe
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  L'ordre sera automatiquement calculé selon la progression
                </p>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de la classe *
                  </label>
                  <input
                    type="text"
                    value={creationData.nom}
                    onChange={(e) => setCreationData(prev => ({ ...prev, nom: e.target.value }))}
                    placeholder="Ex: 6ème A, Terminal D, 1ère ..."
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 outline-none transition-colors"
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 1px ${colors.primary}`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Frais de scolarité</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Frais d'inscription *
                      </label>
                      <input
                        type="number"
                        value={creationData.frais_inscription}
                        onChange={(e) => setCreationData(prev => ({ ...prev, frais_inscription: e.target.value }))}
                        placeholder="0"
                        min="0"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 outline-none transition-colors"
                        onFocus={(e) => {
                          e.target.style.borderColor = colors.primary;
                          e.target.style.boxShadow = `0 0 0 1px ${colors.primary}`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Frais de réinscription
                      </label>
                      <input
                        type="number"
                        value={creationData.frais_reinscription}
                        onChange={(e) => setCreationData(prev => ({ ...prev, frais_reinscription: e.target.value }))}
                        placeholder="0"
                        min="0"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 outline-none transition-colors"
                        onFocus={(e) => {
                          e.target.style.borderColor = colors.primary;
                          e.target.style.boxShadow = `0 0 0 1px ${colors.primary}`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Frais mensuels
                      </label>
                      <input
                        type="number"
                        value={creationData.frais_mensuel}
                        onChange={(e) => setCreationData(prev => ({ ...prev, frais_mensuel: e.target.value }))}
                        placeholder="0"
                        min="0"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 outline-none transition-colors"
                        onFocus={(e) => {
                          e.target.style.borderColor = colors.primary;
                          e.target.style.boxShadow = `0 0 0 1px ${colors.primary}`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Frais optionnels</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Frais de transport
                      </label>
                      <input
                        type="number"
                        value={creationData.frais_transport}
                        onChange={(e) => setCreationData(prev => ({ ...prev, frais_transport: e.target.value }))}
                        placeholder="0"
                        min="0"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 outline-none transition-colors"
                        onFocus={(e) => {
                          e.target.style.borderColor = colors.primary;
                          e.target.style.boxShadow = `0 0 0 1px ${colors.primary}`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Frais de cantine
                      </label>
                      <input
                        type="number"
                        value={hasCantine ? creationData.frais_cantine : ''}
                        onChange={(e) => setCreationData(prev => ({ ...prev, frais_cantine: e.target.value }))}
                        placeholder="0"
                        min="0"
                        disabled={!hasCantine}
                        className={`w-full px-4 py-3 rounded-lg border border-gray-300 outline-none transition-colors ${
                          hasCantine ? '' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                        onFocus={(e) => {
                          if (hasCantine) {
                            e.target.style.borderColor = colors.primary;
                            e.target.style.boxShadow = `0 0 0 1px ${colors.primary}`;
                          }
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      <div className="mt-2">
                        <label className="flex items-center text-sm text-gray-600">
                          <input
                            type="checkbox"
                            checked={!hasCantine}
                            onChange={(e) => {
                              const pasCantine = e.target.checked;
                              setHasCantine(!pasCantine);
                              if (pasCantine) {
                                setCreationData(prev => ({ ...prev, frais_cantine: '' }));
                              }
                            }}
                            className="mr-2 w-4 h-4 border-gray-300 rounded"
                            style={{ accentColor: colors.primary }}
                          />
                          Pas de cantine
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Paramètres de la classe</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Seuil d'élèves par classe
                      </label>
                      {/* Sans le droit des seuils, le champ reste vide : la classe
                          suivra le seuil par défaut (handleCreateClasse envoie null). */}
                      <input
                        type="number"
                        value={droits.seuils ? creationData.seuil_eleves : ''}
                        onChange={(e) => setCreationData(prev => ({ ...prev, seuil_eleves: e.target.value }))}
                        placeholder={seuilGlobal.toString()}
                        min="1"
                        disabled={!droits.seuils}
                        className={`w-full px-4 py-3 rounded-lg border border-gray-300 outline-none transition-colors ${
                          droits.seuils ? '' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                        onFocus={(e) => {
                          e.target.style.borderColor = colors.primary;
                          e.target.style.boxShadow = `0 0 0 1px ${colors.primary}`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      {droits.seuils ? (
                        <p className="text-xs text-gray-500 mt-1">
                          Défaut : {seuilGlobal} élèves. Laissez vide pour utiliser la valeur par défaut.
                        </p>
                      ) : (
                        <p className="text-xs text-amber-700 mt-1">{AIDE_SEUIL_CREATION}</p>
                      )}
                    </div>

                    {/* ✅ NOUVEAU CHAMP TAUX HORAIRE */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Taux horaire enseignants
                      </label>
                      <input
                        type="number"
                        value={creationData.taux_horaire}
                        onChange={(e) => setCreationData(prev => ({ ...prev, taux_horaire: e.target.value }))}
                        placeholder="0"
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 outline-none transition-colors"
                        onFocus={(e) => {
                          e.target.style.borderColor = colors.primary;
                          e.target.style.boxShadow = `0 0 0 1px ${colors.primary}`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Taux horaire pour les enseignants de cette classe (en FCFA/heure).
                      </p>
                    </div>

                    {/* La case de KLASYS (ui/CaseACocher, DESIGN-SYSTEM.md). */}
                    <div className="md:col-span-2 overflow-hidden rounded-lg border border-gray-200">
                      <CaseACocher
                        checked={droits.associer && creationData.sans_matiere}
                        onChange={(coche) => setCreationData(prev => ({ ...prev, sans_matiere: coche }))}
                        disabled={!droits.associer}
                        label="Classe sans matière"
                        description="Pour les classes comme garderie ou activités sans enseignements académiques."
                        aide={droits.associer ? null : AIDE_SANS_MATIERE}
                      />
                    </div>
                  </div>
                </div>

                <div
                  className="rounded-lg p-4 border"
                  style={{ backgroundColor: colors.light, borderColor: colors.secondary + '40' }}
                >
                  <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                    Résumé financier
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Inscription</p>
                      <p className="font-semibold">{formatPrice(creationData.frais_inscription)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Mensuel</p>
                      <p className="font-semibold">{formatPrice(creationData.frais_mensuel)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Transport</p>
                      <p className="font-semibold">{formatPrice(creationData.frais_transport)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Cantine</p>
                      <p className="font-semibold">
                        {hasCantine ? formatPrice(creationData.frais_cantine) : 'Non applicable'}
                      </p>
                    </div>
                    {/* ✅ NOUVEAU RÉSUMÉ TAUX HORAIRE */}
                    <div>
                      <p className="text-gray-600">Taux horaire</p>
                      <p className="font-semibold text-blue-600">
                        {creationData.taux_horaire ? `${formatPrice(creationData.taux_horaire)}/h` : 'Non défini'}
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-6 border-t">
              <button
                onClick={() => setActiveView('sections')}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                onClick={handleCreateClasse}
                disabled={loading || !creationData.nom.trim() || !creationData.section_id}
                className="px-8 py-3 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.secondary}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Création...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer la classe
                  </>
                )}
              </button>
            </div>
          </div>
        );
      })()}

      {editingClasse && (
        <EditModal
          classe={editingClasse}
          onClose={() => setEditingClasse(null)}
          onUpdate={handleUpdateClasse}
          // Les deux fenêtres sont des Dialog : la demande s'ouvre par-dessus la fiche.
          onDemanderTaux={(classe) => setTauxClasse(classe)}
        />
      )}

      <ModalTauxHoraireClasse
        classe={tauxClasse}
        open={Boolean(tauxClasse)}
        onClose={() => setTauxClasse(null)}
        onEnvoyee={(message) => addToast(message, 'success')}
      />

      {showSeuilGlobalModal && droits.seuils && (
        <div className={`${CONFIRM_DIALOG_OVERLAY_CLASS} flex items-center justify-center p-4`}>
          <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl">
            <div className="flex items-start justify-between gap-4 border-b border-[#246990]/10 bg-[#246990]/5 px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white shadow-sm">
                  <Gauge className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-950">Seuil par défaut</h3>
                  <p className="mt-1 text-sm text-gray-500">Capacité appliquée aux nouvelles classes.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowSeuilGlobalModal(false)}
                className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-white hover:text-[#246990]"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5">
              <label className="block text-sm font-medium text-gray-700" htmlFor="seuil-global-input">
                Nombre d'élèves maximum par classe
              </label>
              <div className="mt-2 flex items-center rounded-lg border border-gray-300 bg-white px-4 py-3 transition-colors focus-within:border-[#246990] focus-within:ring-4 focus-within:ring-[#246990]/10">
                <input
                  id="seuil-global-input"
                  type="number"
                  value={seuilGlobal}
                  onChange={(e) => setSeuilGlobal(parseInt(e.target.value, 10) || 25)}
                  min="1"
                  max="100"
                  className="w-full bg-transparent text-base font-semibold text-gray-950 outline-none"
                />
                <span className="ml-3 whitespace-nowrap text-sm font-medium text-gray-500">élèves</span>
              </div>
              <div className="mt-4 rounded-lg border border-[#246990]/10 bg-[#246990]/5 px-4 py-3 text-sm leading-6 text-gray-600">
                Ce seuil sera utilisé comme valeur initiale pour les prochaines classes. Chaque classe pourra ensuite garder ce seuil ou recevoir un seuil personnalisé.
              </div>

              <div className="mt-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990]/10 text-[#246990]">
                    <Layers className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-semibold text-gray-950">Quand le seuil est atteint</h4>
                    <p className="mt-1 text-sm leading-6 text-gray-500">
                      Choisissez si l'inscription doit etre bloquee ou si une classe annexe doit etre creee automatiquement.
                    </p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setAutoCreationAnnexeSeuil(false)}
                    className={(autoCreationAnnexeSeuil
                      ? 'border-gray-200 bg-white text-gray-600 hover:border-[#246990]/30 hover:text-[#246990]'
                      : 'border-[#246990] bg-[#246990]/10 text-[#246990]') + ' rounded-lg border px-4 py-3 text-left text-sm font-semibold transition-colors'}
                  >
                    Bloquer l'inscription
                  </button>
                  <button
                    type="button"
                    onClick={() => setAutoCreationAnnexeSeuil(true)}
                    className={(autoCreationAnnexeSeuil
                      ? 'border-[#246990] bg-[#246990]/10 text-[#246990]'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-[#246990]/30 hover:text-[#246990]') + ' rounded-lg border px-4 py-3 text-left text-sm font-semibold transition-colors'}
                  >
                    Creer une annexe
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowSeuilGlobalModal(false)}
                className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-[#246990]/30 hover:text-[#246990]"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    addToast('Mise à jour du seuil par défaut en cours...', 'loading');
                    const result = await api.classes.updateSeuilGlobal(seuilGlobal, {
                      auto_creation_annexe_seuil: autoCreationAnnexeSeuil
                    });
                    if (result.success) {
                      addToast('Seuil par défaut mis à jour', 'success');
                      setShowSeuilGlobalModal(false);
                    } else {
                      if (estRefusDeDroit(result)) setShowSeuilGlobalModal(false);
                      addToast(messageEchec(result, 'Erreur lors de la mise à jour'), 'error');
                    }
                  } catch (error) {
                    addToast(getErrorMessage(error, 'Erreur lors de la mise à jour'), 'error');
                  }
                }}
                className="inline-flex items-center justify-center rounded-lg bg-[#246990] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1f5a7d]"
              >
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        type={confirmDialog.type}
      />
    </div>
  );
};

export default ClassesModernes;
